import { useMemo, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { themes } from '../../theme';
import { Icon } from './Icon';
import { Avatar } from './Avatar';
import { Sheet } from '../shared/Sheet';
import { useT } from '../../i18n';
import { useBabies, useBabyAccess, useBabyMilestones, useMe, useUpdateProfile } from '../../data/queries';
import { PlanSection } from '../upgrade/PlanSection';
import { useBilling } from '../../billing/BillingProvider';
import { qk } from '../../data/keys';
import { api } from '../../api';
import { BabyFormSheet } from '../profile/BabyFormSheet';
import { SharingSheet } from '../profile/SharingSheet';
import { JoinBabySheet } from '../profile/JoinBabySheet';
import { FeedbackSheet } from '../profile/FeedbackSheet';
import { AccountSettingsSheet } from '../profile/AccountSettingsSheet';
import { DeleteAccountSheet } from '../profile/DeleteAccountSheet';
import { ApiKeySheet } from '../profile/ApiKeySheet';
import { useAuth } from '../../auth/AuthProvider';
import { authMode } from '../../auth/firebase';
import type { BabyFormData } from '../profile/BabyFormSheet';
import type { Baby } from '../../data/types';

interface Props {
  open: boolean;
  baby: Baby;
  babies: Baby[];
  onSwitchBaby: (id: string) => void;
  onAddBaby: (data: BabyFormData) => void;
  onEditBaby: (data: BabyFormData) => void;
  onSignOut?: () => void;
  onClose: () => void;
  onOpenMedia?: () => void;
  onOpenSettings?: () => void;
  adding?: boolean;
  editing?: boolean;
  /** Legacy ignored. */
  accent?: string;
  accentInk?: string;
}

export function ProfileSheet({
  open,
  baby,
  babies,
  onSwitchBaby,
  onAddBaby,
  onEditBaby,
  onSignOut,
  onClose,
  onOpenMedia,
  onOpenSettings,
  adding,
  editing,
}: Props) {
  const queryClient = useQueryClient();
  const babiesQ = useBabies(open);
  const t = useT();

  const me = useMe();
  const user = me.data?.user;
  const auth = useAuth();
  const { openPaywall } = useBilling();
  const accessQ = useBabyAccess(open && baby.id ? baby.id : null);

  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [accountSettingsMode, setAccountSettingsMode] = useState<'email' | 'password'>('email');
  const [showApiKeySheet, setShowApiKeySheet] = useState(false);

  const updateProfile = useUpdateProfile();
  const [realName, setRealName] = useState('');
  const [relation, setRelation] = useState('');

  useEffect(() => {
    if (open) {
      setRealName(me.data?.profile?.name ?? '');
      setRelation(me.data?.profile?.relation ?? '');
    }
  }, [open, me.data?.profile?.name, me.data?.profile?.relation]);

  const handleSaveProfile = (patch: Record<string, unknown>) => {
    updateProfile.mutate(patch, {
      onError: (e) => console.error('Failed to update profile:', e),
    });
  };

  const handleOpenAccountSettings = (m: 'email' | 'password') => {
    setAccountSettingsMode(m);
    setShowAccountSettings(true);
  };

  // Automatically check verification status when Profile Sheet opens
  useEffect(() => {
    if (open && user && user.email_verified_at === null && authMode === 'firebase') {
      let active = true;
      (async () => {
        try {
          const { getFirebaseAuth } = await import('../../auth/firebase');
          const fbAuth = await getFirebaseAuth();
          const currentUser = fbAuth.currentUser;
          if (currentUser && active) {
            await currentUser.reload();
            if (currentUser.emailVerified && active) {
              const token = await currentUser.getIdToken(true);
              await api('/auth/verify', {
                method: 'POST',
                body: { id_token: token },
              });
              await queryClient.invalidateQueries({ queryKey: qk.me() });
            }
          }
        } catch (e) {
          console.error('Failed to auto-check verification status:', e);
        }
      })();
      return () => {
        active = false;
      };
    }
  }, [open, user?.email_verified_at, queryClient]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((c) => c - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleCheckVerificationStatus = async () => {
    if (authMode === 'mock') return;
    setResending(true);
    setFeedbackMsg(null);
    try {
      const { getFirebaseAuth } = await import('../../auth/firebase');
      const fbAuth = await getFirebaseAuth();
      const currentUser = fbAuth.currentUser;
      if (currentUser) {
        await currentUser.reload();
        if (currentUser.emailVerified) {
          const token = await currentUser.getIdToken(true);
          await api('/auth/verify', {
            method: 'POST',
            body: { id_token: token },
          });
          await queryClient.invalidateQueries({ queryKey: qk.me() });
          setFeedbackMsg(t('auth.verification.alreadyVerified', 'Your email is now verified!'));
        } else {
          setFeedbackMsg(
            t(
              'auth.verification.notVerifiedYet',
              "Email verification link hasn't been clicked yet. Please click the link in your email and try again."
            )
          );
        }
      }
    } catch (e) {
      console.error(e);
      setFeedbackMsg(t('auth.verification.checkError', 'Failed to check verification status.'));
    } finally {
      setResending(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    setFeedbackMsg(null);
    try {
      if (authMode === 'mock') {
        setFeedbackMsg(t('auth.verification.mockSent', 'Mock verification email sent!'));
        setCooldown(60);
      } else {
        const { getFirebaseAuth } = await import('../../auth/firebase');
        const fbAuth = await getFirebaseAuth();
        const currentUser = fbAuth.currentUser;
        if (currentUser) {
          await currentUser.reload();
          if (currentUser.emailVerified) {
            const token = await currentUser.getIdToken(true);
            await api('/auth/verify', {
              method: 'POST',
              body: { id_token: token },
            });
            await queryClient.invalidateQueries({ queryKey: qk.me() });
            setFeedbackMsg(t('auth.verification.alreadyVerified', 'Your email is now verified!'));
            return;
          }
        }

        await auth.resendVerificationEmail();
        setFeedbackMsg(t('auth.verification.sentSuccess', 'Verification email sent successfully!'));
        setCooldown(60);
      }
    } catch (e) {
      console.error(e);
      setFeedbackMsg(t('auth.verification.resendError', 'Could not resend verification email.'));
    } finally {
      setResending(false);
    }
  };

  // Sharing button is visible if active baby id is present
  const canShare = !!baby.id;

  const currentTier = me.data?.entitlements?.tier ?? 'free';
  const isFree = currentTier === 'free';
  const hasAlreadyBaby = babies.length > 0;

  const membersLimit = me.data?.entitlements?.limits?.members_per_baby ?? 1;
  const currentMembersCount = accessQ.data?.filter((m) => m.role !== 'owner').length ?? 0;
  const isSharingLimitReached = currentMembersCount >= membersLimit;

  // Inline edit state for the active baby hero
  const [isEditingHero, setIsEditingHero] = useState(false);
  const [editName, setEditName] = useState(baby.name);
  const [editDob, setEditDob] = useState(baby.dobIso ?? '');

  // Reset inline edit state when baby changes or sheet opens
  const resetEdit = () => {
    setEditName(baby.name);
    setEditDob(baby.dobIso ?? '');
    setIsEditingHero(false);
  };

  // Add-baby / sharing / join sheets
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [showSharing, setShowSharing] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showFeedbackSheet, setShowFeedbackSheet] = useState(false);
  const [showSubscriptionAlert, setShowSubscriptionAlert] = useState(false);
  const [showSharingLimitAlert, setShowSharingLimitAlert] = useState(false);

  const handleSaveEdit = () => {
    const dobValid = /^\d{4}-\d{2}-\d{2}$/.test(editDob) && new Date(editDob).toString() !== 'Invalid Date';
    const nameValid = editName.trim().length > 0;
    if (!nameValid || !dobValid) return;
    onEditBaby({ name: editName.trim(), dob: editDob, gender: baby.preset ?? 'neutral' });
    setIsEditingHero(false);
  };

  const handleAddBabyClick = () => {
    if (isFree && hasAlreadyBaby) {
      setShowSubscriptionAlert(true);
    } else {
      setShowAddSheet(true);
    }
  };

  const handleJoinBabyClick = () => {
    if (isFree && hasAlreadyBaby) {
      setShowSubscriptionAlert(true);
    } else {
      setShowJoin(true);
    }
  };

  const handleSharingClick = () => {
    if (isSharingLimitReached) {
      setShowSharingLimitAlert(true);
    } else {
      setShowSharing(true);
    }
  };

  return (
    <>
      <Sheet open={open} onClose={onClose} size="lg" maxHeight="92dvh" label={t('profile.title', 'Profile')}>
        <div className="flex flex-col gap-7 overflow-y-auto px-6 pb-8 pt-0">
          {/* Close-row */}
          <div className="flex items-center justify-between">
            <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
              {t('profile.title', 'Profile')}
            </span>
            <button
              onClick={onClose}
              aria-label={t('common.close', 'Close profile')}
              className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
            >
              <Icon name="close" size={22} color="var(--color-ink)" />
            </button>
          </div>

          {/* 1. Top: Your Account */}
          <div>
            <h2 className="mb-2.5 font-serif text-lg tracking-tight text-ink">
              {t('profile.accountTitle', 'Your Account')}
            </h2>
            {user && (
              <div className="rounded-2xl border border-line bg-surface p-4 shadow-xs">
                <span className="text-[10px] font-bold uppercase tracking-wider block text-ink-3 mb-1">
                  {t('profile.account', 'Your Account')}
                </span>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="block font-semibold text-[14.5px] text-ink truncate">{user.email}</span>
                    {user.email_verified_at ? (
                      <span className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-600 mt-0.5">
                        ✓ {t('profile.verified', 'Verified')}
                      </span>
                    ) : (
                      <button
                        onClick={handleCheckVerificationStatus}
                        disabled={resending}
                        className="inline-flex items-center gap-1 text-[12px] font-medium text-amber-600 mt-0.5 hover:underline text-left cursor-pointer disabled:opacity-50"
                      >
                        ⚠️ {t('profile.unverified', 'Unverified (Click to check)')}
                      </button>
                    )}
                  </div>
                  {!user.email_verified_at && (
                    <button
                      onClick={handleResendVerification}
                      disabled={resending || cooldown > 0}
                      className="shrink-0 rounded-full border border-line bg-surface px-3 py-1.5 text-[12px] font-semibold text-ink-2 active:scale-95 transition-all disabled:opacity-50 cursor-pointer"
                    >
                      {resending ? (
                        <span className="inline-block w-4 h-4 border-2 border-ink-2 border-t-transparent animate-spin rounded-full" />
                      ) : cooldown > 0 ? (
                        `${cooldown}s`
                      ) : (
                        t('profile.resendEmail', 'Resend Link')
                      )}
                    </button>
                  )}
                </div>
                {feedbackMsg && (
                  <div className="mt-2 text-[12px] text-ink-2">
                    {feedbackMsg}
                  </div>
                )}

                {/* Name & Relation/Role editing fields */}
                <div className="mt-4 pt-4 border-t border-hairline flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="profile-real-name" className="text-[11px] font-bold uppercase tracking-wider text-ink-3">
                      {t('profile.realName', 'Name')}
                    </label>
                    <input
                      id="profile-real-name"
                      type="text"
                      value={realName}
                      onChange={(e) => setRealName(e.target.value)}
                      onBlur={() => handleSaveProfile({ name: realName })}
                      placeholder={t('profile.realNamePlaceholder', 'Your name (optional)')}
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-[14px] text-ink outline-none focus:border-ink transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="profile-relation" className="text-[11px] font-bold uppercase tracking-wider text-ink-3">
                      {t('profile.relation', 'Relation / Role')}
                    </label>
                    <select
                      id="profile-relation"
                      value={relation}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRelation(val);
                        handleSaveProfile({ relation: val === '' ? null : val });
                      }}
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-[14px] text-ink outline-none focus:border-ink transition-colors"
                    >
                      <option value="">{t('profile.relation.none', 'Not specified')}</option>
                      <option value="mother">{t('profile.relation.mother', 'Mother')}</option>
                      <option value="father">{t('profile.relation.father', 'Father')}</option>
                      <option value="grandmother">{t('profile.relation.grandmother', 'Grandmother')}</option>
                      <option value="grandfather">{t('profile.relation.grandfather', 'Grandfather')}</option>
                      <option value="aunt">{t('profile.relation.aunt', 'Aunt')}</option>
                      <option value="uncle">{t('profile.relation.uncle', 'Uncle')}</option>
                      <option value="sister">{t('profile.relation.sister', 'Sister')}</option>
                      <option value="brother">{t('profile.relation.brother', 'Brother')}</option>
                      <option value="cousin">{t('profile.relation.cousin', 'Cousin')}</option>
                      <option value="godmother">{t('profile.relation.godmother', 'Godmother')}</option>
                      <option value="godfather">{t('profile.relation.godfather', 'Godfather')}</option>
                      <option value="nanny">{t('profile.relation.nanny', 'Nanny')}</option>
                      <option value="carer">{t('profile.relation.carer', 'Carer')}</option>
                      <option value="relative">{t('profile.relation.relative', 'Relative')}</option>
                      <option value="friend">{t('profile.relation.friend', 'Friend')}</option>
                    </select>
                  </div>
                </div>

                {/* Account management actions */}
                <div className="mt-3.5 pt-3.5 border-t border-hairline flex flex-wrap gap-x-3.5 gap-y-1.5 items-center text-[12.5px] font-semibold text-ink-2">
                  {(!auth.user?.providerId || auth.user.providerId === 'password') && (
                    <>
                      <button
                        onClick={() => handleOpenAccountSettings('email')}
                        className="hover:text-ink transition-colors cursor-pointer"
                      >
                        {t('profile.changeEmail', 'Change Email')}
                      </button>
                      <span className="text-line select-none">|</span>
                      <button
                        onClick={() => handleOpenAccountSettings('password')}
                        className="hover:text-ink transition-colors cursor-pointer"
                      >
                        {t('profile.changePassword', 'Change Password')}
                      </button>
                      <span className="text-line select-none">|</span>
                    </>
                  )}
                  <button
                    onClick={() => setShowApiKeySheet(true)}
                    className="hover:text-ink transition-colors cursor-pointer"
                  >
                    {t('profile.apiKeys', 'API Keys')}
                  </button>
                </div>
                {(auth.user?.providerId === 'google.com' || auth.user?.providerId === 'apple.com') && (
                  <div className="mt-1.5 text-[11.5px] text-ink-3 italic">
                    {t(
                      'profile.providerManaged',
                      'Linked with {provider}. Manage credentials via your provider.'
                    ).replace('{provider}', auth.user.providerId === 'google.com' ? 'Google' : 'Apple')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3. Settings (moved below Your Account box) */}
          {onOpenSettings && (
            <button
              onClick={() => {
                onClose();
                onOpenSettings();
              }}
              className="inline-flex w-full min-h-13 items-center justify-center gap-2 rounded-3xl border border-line bg-surface text-sm font-semibold text-ink-2 hover:text-ink transition-colors"
            >
              <Icon name="settings" size={16} color="var(--color-ink-2)" />
              {t('settings.title', 'Settings')}
            </button>
          )}

          {/* 2. Plan */}
          <PlanSection />

          {/* HR Divider between Plan and the active baby */}
          <hr className="border-t border-line" />

          {/* 4. the active baby */}
          {/* Hero — editable inline */}
          <div className="flex items-center gap-4">
            <Avatar size={72} baby={baby} />
            <div className="min-w-0 flex-1">
              {isEditingHero ? (
                <div className="flex flex-col gap-2">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder={t('profile.form.nameLabel', 'Name')}
                    className="w-full border-0 border-b-[1.5px] border-ink bg-transparent pb-1 font-serif text-[22px] tracking-tight text-ink outline-none placeholder:text-ink-3"
                  />
                  <input
                    type="date"
                    value={editDob}
                    onChange={(e) => setEditDob(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="w-full border-0 bg-transparent text-[13px] text-ink outline-none"
                  />
                  <div className="flex gap-2 mt-1">
                    <button
                      onClick={handleSaveEdit}
                      disabled={editing}
                      className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1 text-[12px] font-semibold text-bg disabled:opacity-50"
                    >
                      {editing ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                    </button>
                    <button
                      onClick={resetEdit}
                      className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1 text-[12px] font-semibold text-ink-2"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="font-serif text-[28px] leading-none tracking-tight text-ink">
                    {baby.name}
                  </h1>
                  <p className="mt-1 text-[13px] text-ink-2">
                    {t('profile.bornWeeks', 'Born {dob} · {weeks} weeks old').replace('{dob}', baby.dob).replace('{weeks}', String(baby.ageWeeks))}
                  </p>
                </>
              )}
            </div>
            {!isEditingHero && canShare && (
              <button
                onClick={() => {
                  setEditName(baby.name);
                  setEditDob(baby.dobIso ?? '');
                  setIsEditingHero(true);
                }}
                aria-label={t('profile.editAria', 'Edit profile')}
                className="flex min-h-10 min-w-10 items-center justify-center rounded-full border border-line bg-surface text-ink"
              >
                <Icon name="edit" size={16} color="var(--color-ink)" />
              </button>
            )}
          </div>

          {/* 5. your babies */}
          <section>
            <h2 className="mb-2.5 font-serif text-lg tracking-tight text-ink">{t('profile.babies', 'Your babies')}</h2>
            <div className="overflow-hidden rounded-2xl border border-line bg-surface">
              {babies.map((bb, i, a) => {
                const isCurrent = bb.id === baby.id;
                const preset = bb.preset ?? 'neutral';
                const bAccent = themes[preset].accent;
                const bAccentInk = themes[preset].accentInk;
                return (
                  <button
                    key={bb.id}
                    onClick={() => onSwitchBaby(bb.id ?? '')}
                    className={[
                      'flex min-h-11 w-full items-center gap-3 px-3.5 py-3 text-left',
                      i < a.length - 1 ? 'border-b border-hairline' : '',
                    ].join(' ')}
                    style={
                      isCurrent
                        ? { background: `color-mix(in oklch, ${bAccent} 7%, var(--color-surface))` }
                        : undefined
                    }
                  >
                    <Avatar size={40} baby={bb} />
                    <span className="min-w-0 flex-1">
                      <span className="block font-serif text-[17px] leading-tight tracking-tight text-ink">
                        {bb.name}
                      </span>
                      <span className="mt-px block text-[11.5px] text-ink-2">
                        {t('profile.bornWks', 'Born {dob} · {weeks} wks').replace('{dob}', bb.dob).replace('{weeks}', String(bb.ageWeeks))}
                      </span>
                    </span>
                    {isCurrent ? (
                      <span
                        className="rounded-full px-2 py-0.75 text-[10px] font-bold uppercase tracking-wider"
                        style={{
                          color: bAccentInk,
                          background: `color-mix(in oklch, ${bAccent} 16%, var(--color-bg))`,
                        }}
                      >
                        {t('profile.activeBaby', 'Active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-ink-2">
                        {t('profile.switchBaby', 'Switch')} <Icon name="switch" size={14} color="var(--color-ink-2)" />
                      </span>
                    )}
                  </button>
                );
              })}
              <button
                onClick={handleAddBabyClick}
                className="flex min-h-11 w-full items-center gap-3 border-t border-hairline bg-transparent px-3.5 py-3 text-left"
              >
                <span className="flex size-10 items-center justify-center rounded-full border border-dashed border-line">
                  <Icon name="plus" size={18} color="var(--color-ink-2)" />
                </span>
                <span className="text-[14.5px] font-medium text-ink-2">{t('profile.addAnotherBaby', 'Add another baby')}</span>
              </button>
              <button
                onClick={handleJoinBabyClick}
                className="flex min-h-11 w-full items-center gap-3 border-t border-hairline bg-transparent px-3.5 py-3 text-left"
              >
                <span className="flex size-10 items-center justify-center rounded-full border border-dashed border-line">
                  <Icon name="switch" size={18} color="var(--color-ink-2)" />
                </span>
                <span className="text-[14.5px] font-medium text-ink-2">{t('profile.joinBaby', 'Join a baby')}</span>
              </button>
            </div>

            {/* ** Sharing & access */}
            {canShare && (
              <button
                onClick={handleSharingClick}
                className="mt-2.5 flex min-h-12 w-full items-center gap-3 rounded-2xl border border-line bg-surface px-3.5 py-3 text-left"
              >
                <span className="flex size-10 items-center justify-center rounded-full border border-line">
                  <Icon name="profile" size={18} color="var(--color-ink-2)" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14.5px] font-semibold text-ink">{t('sharing.title', 'Sharing & access')}</span>
                  <span className="block text-[12px] text-ink-2">
                    {t('sharing.rowHint', 'Invite a partner or carer to {name}').replace('{name}', baby.name)}
                  </span>
                </span>
                <Icon name="chevron" size={16} color="var(--color-ink-3)" />
              </button>
            )}
          </section>

          {/* 6. Media Gallery */}
          {onOpenMedia && (
            <button
              onClick={() => {
                onClose();
                onOpenMedia();
              }}
              className="inline-flex w-full min-h-13 items-center justify-center gap-2 rounded-3xl border border-line bg-surface text-sm font-semibold text-ink-2 hover:text-ink transition-colors"
            >
              <Icon name="photos" size={16} color="var(--color-ink-2)" />
              {t('profile.mediaGallery', 'Media Gallery')}
            </button>
          )}

          {/* 7. Report bug or suggested feature */}
          <button
            onClick={() => setShowFeedbackSheet(true)}
            className="inline-flex w-full min-h-13 items-center justify-center gap-2 rounded-3xl border border-line bg-surface text-sm font-semibold text-ink-2 hover:text-ink transition-colors"
          >
            <Icon name="edit" size={16} color="var(--color-ink-2)" />
            {t('profile.feedback', 'Report bug or suggest feature')}
          </button>

          {/* 8. Sign out */}
          <button
            onClick={onSignOut}
            className="inline-flex w-full min-h-13 items-center justify-center gap-2 rounded-3xl border border-line bg-surface text-sm font-semibold text-ink-2 hover:text-ink transition-colors"
          >
            <Icon name="signout" size={16} color="var(--color-ink-2)" />
            {t('auth.signOut', 'Sign out')}
          </button>

          {/* 9. Delete account */}
          <button
            onClick={() => setShowDeleteAccount(true)}
            className="inline-flex w-full min-h-13 items-center justify-center gap-2 rounded-3xl border border-line bg-surface text-sm font-semibold text-[#B5523B] hover:opacity-80 transition-opacity"
          >
            {t('account.deletion.entryPoint', 'Delete Account')}
          </button>
        </div>
      </Sheet>

      <BabyFormSheet
        open={showAddSheet}
        mode="add"
        onSave={(data) => {
          onAddBaby(data);
          setShowAddSheet(false);
        }}
        onClose={() => setShowAddSheet(false)}
        saving={adding}
      />

      {baby.id && (
        <SharingSheet
          open={showSharing}
          babyId={baby.id}
          babyName={baby.name}
          onClose={() => setShowSharing(false)}
        />
      )}

      <JoinBabySheet
        open={showJoin}
        onClose={() => setShowJoin(false)}
        onJoined={(id) => {
          setShowJoin(false);
          onSwitchBaby(id);
        }}
      />

      <FeedbackSheet
        open={showFeedbackSheet}
        onClose={() => setShowFeedbackSheet(false)}
      />

      <AccountSettingsSheet
        open={showAccountSettings}
        initialMode={accountSettingsMode}
        onClose={() => setShowAccountSettings(false)}
      />

      <DeleteAccountSheet
        open={showDeleteAccount}
        onClose={() => setShowDeleteAccount(false)}
      />

      <ApiKeySheet
        open={showApiKeySheet}
        onClose={() => setShowApiKeySheet(false)}
      />

      {/* Subscription Required Alert Sheet */}
      <Sheet
        open={showSubscriptionAlert}
        onClose={() => setShowSubscriptionAlert(false)}
        size="sm"
        noGrabber
        label={t('profile.subscriptionRequired', 'Subscription Required')}
      >
        <div className="flex flex-col gap-6 px-6 pb-8 pt-6 overflow-y-auto text-center">
          <div className="flex justify-end -mt-4 -mr-2">
            <button
              onClick={() => setShowSubscriptionAlert(false)}
              aria-label={t('common.close', 'Close')}
              className="p-1 text-ink-3 hover:text-ink"
            >
              <Icon name="close" size={20} />
            </button>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
              <Icon name="trophy" size={28} />
            </div>
            <h2 className="font-serif text-[24px] leading-tight tracking-tight text-ink">
              {t('profile.activeSubRequiredTitle', 'Active Subscription Required')}
            </h2>
            <p className="text-[14px] leading-relaxed text-ink-2 text-pretty">
              {t('profile.activeSubRequiredDesc', 'this is only available with an active subscription')}
            </p>
          </div>
          <div className="flex flex-col gap-2 mt-2">
            <button
              onClick={() => {
                setShowSubscriptionAlert(false);
                openPaywall();
              }}
              className="inline-flex min-h-12 items-center justify-center rounded-3xl bg-ink px-6 text-[14.5px] font-semibold text-bg active:scale-[0.98] transition-transform"
            >
              {t('profile.showPlan', 'Show Plan')}
            </button>
            <button
              onClick={() => setShowSubscriptionAlert(false)}
              className="inline-flex min-h-12 items-center justify-center rounded-3xl border border-line bg-transparent px-6 text-[14.5px] font-semibold text-ink-2 hover:text-ink active:scale-[0.98] transition-all"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </Sheet>

      {/* Sharing Limit Reached Alert Sheet */}
      <Sheet
        open={showSharingLimitAlert}
        onClose={() => setShowSharingLimitAlert(false)}
        size="sm"
        noGrabber
        label={t('profile.sharingLimitReached', 'Sharing Limit Reached')}
      >
        <div className="flex flex-col gap-6 px-6 pb-8 pt-6 overflow-y-auto text-center">
          <div className="flex justify-end -mt-4 -mr-2">
            <button
              onClick={() => setShowSharingLimitAlert(false)}
              aria-label={t('common.close', 'Close')}
              className="p-1 text-ink-3 hover:text-ink"
            >
              <Icon name="close" size={20} />
            </button>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-600">
              <Icon name="profile" size={28} color="currentColor" />
            </div>
            <h2 className="font-serif text-[24px] leading-tight tracking-tight text-ink">
              {t('profile.limitReachedTitle', 'Sharing Limit Reached')}
            </h2>
            <p className="text-[14px] leading-relaxed text-ink-2 text-pretty">
              {t('profile.limitReachedDesc', 'you have reached your limit')}
            </p>
          </div>
          <div className="flex flex-col gap-2 mt-2">
            <button
              onClick={() => {
                setShowSharingLimitAlert(false);
                openPaywall({ feature: 'members_per_baby' });
              }}
              className="inline-flex min-h-12 items-center justify-center rounded-3xl bg-ink px-6 text-[14.5px] font-semibold text-bg active:scale-[0.98] transition-transform"
            >
              {t('profile.showPlan', 'Show Plan')}
            </button>
            <button
              onClick={() => setShowSharingLimitAlert(false)}
              className="inline-flex min-h-12 items-center justify-center rounded-3xl border border-line bg-transparent px-6 text-[14.5px] font-semibold text-ink-2 hover:text-ink active:scale-[0.98] transition-all"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </Sheet>
    </>
  );
}
